/**
 * Freeform-block adapter (UX Pack F4b): the direct children of a freeform
 * section's `.cs-container` are the units — mixed kinds by design (a heading,
 * an image, and a card multi-select together), which is why this is one
 * adapter over ALL block roots rather than per-block-type adapters.
 *
 * C7-safe by construction: `itemSelector` is only ever consumed via
 * `closest()` and `parentElement.children.filter(matches(...))`
 * (siblingUnitsNearEid) — never concatenated onto `':scope > '`.
 *
 * Kind resolution is CENTRALIZED in resolveAdapterKindForPayload: chainProbe
 * cannot express `section[id^="freeform-"]` (its parser handles tag+class
 * compounds only), so payload resolution falls back to the section TYPE via
 * instanceId — one resolver shared by element-selection and the context menu,
 * never duplicated.
 */

import { resolveSectionByInstance } from '../section-utils.js';
import { chainProbe, resolveAdapter } from '../pattern-adapters.js';

export const FREEFORM_BLOCK_KIND = 'freeform-block';

export const freeformBlockAdapter = {
  kind: FREEFORM_BLOCK_KIND,
  label: 'Block',
  itemSelector: 'section > .cs-container > *',
  parentSelector: '.cs-container',
  menuActions: ['duplicate', 'delete'],
  postInsertFlow: null,
  cssPatch: null,
  contentRecipe: null,
  reorderable: false,
  moveFn: null,

  match(probe) {
    // Live-DOM probes only. chainProbe exposes closest() but not matches(),
    // and its selector parser cannot express the attribute selector anyway —
    // chain payloads resolve through resolveAdapterKindForPayload instead.
    if (!probe || typeof probe.closest !== 'function' || typeof probe.matches !== 'function') {
      return null;
    }
    try {
      if (!probe.closest('section[id^="freeform-"]')) return null;
      return probe.closest('section > .cs-container > *');
    } catch {
      return null;
    }
  }
};

/**
 * The freeform fallback kind for a payload's section: 'freeform-block' when
 * the instance is a freeform section, else null.
 */
export function resolveFreeformAdapterKind(instanceId) {
  if (!instanceId) return null;
  const section = resolveSectionByInstance(instanceId)?.section;
  return section?.type === 'freeform' ? FREEFORM_BLOCK_KIND : null;
}

/**
 * THE adapter-kind resolver for click/context payloads: registered adapters
 * via the ancestor chain first (unchanged behavior everywhere), then the
 * freeform section-type fallback. Both element-selection and the context menu
 * consume this — the fallback logic lives in exactly one place.
 */
export function resolveAdapterKindForPayload(payload) {
  const chainKind = resolveAdapter(chainProbe(payload?.ancestorChain || []))?.kind || null;
  if (chainKind) return chainKind;
  return resolveFreeformAdapterKind(payload?.instanceId);
}
